// MessageCard.tsx - Message Card Component
// Agent ve user mesaj karti

import { Bot, Smile, User } from 'lucide-react'
import { useState } from 'react'
import ReactionPicker from './ReactionPicker'

interface Message {
  id: number
  type: 'family' | 'user'
  project_id: number
  message_text: string
  created_at: string

  // Family message fields
  agent_id?: number
  expert_id?: string
  cycle_number?: number
  message_type?: string

  // User message fields
  user_id?: number
  is_donor?: boolean

  // Reactions
  reactions?: Record<string, number>
}

interface MessageCardProps {
  message: Message
  currentUserId: number
  onReaction: (messageId: number, messageType: 'family' | 'user', emoji: string) => void
}

export default function MessageCard({ message, onReaction }: MessageCardProps) {
  const [showReactionPicker, setShowReactionPicker] = useState(false)

  const isAgentMessage = message.type === 'family'

  // Uzman isimleri (expert_id -> isim)
  const expertNames: Record<string, string> = {
    'feynman': 'Richard Feynman',
    'darwin': 'Charles Darwin',
    'einstein': 'Albert Einstein',
    'newton': 'Isaac Newton',
    'gauss': 'Carl Friedrich Gauss',
    'turing': 'Alan Turing',
    'curie': 'Marie Curie',
    'hawking': 'Stephen Hawking'
  }

  const getExpertName = (expertId?: string) => {
    if (!expertId) {return 'Agent'}
    return expertNames[expertId] || expertId
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) {return 'Simdi'}
    if (diffMins < 60) {return `${diffMins} dakika once`}

    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) {return `${diffHours} saat once`}

    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 7) {return `${diffDays} gun once`}

    return date.toLocaleDateString('tr-TR')
  }

  const handleReactionClick = (emoji: string) => {
    onReaction(message.id, message.type, emoji)
    setShowReactionPicker(false)
  }

  return (
    <div style={{
      display: 'flex',
      gap: '12px',
      padding: '12px',
      borderRadius: '8px',
      background: isAgentMessage ? 'var(--card-bg)' : 'transparent',
      border: isAgentMessage ? '1px solid var(--border-color)' : 'none',
      position: 'relative'
    }}>
      {/* Avatar */}
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: isAgentMessage ? 'var(--primary-color)' : 'var(--secondary-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        {isAgentMessage ? (
          <Bot size={20} color="white" />
        ) : (
          <User size={20} color="white" />
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1 }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '4px'
        }}>
          <span style={{
            fontWeight: 600,
            fontSize: '14px',
            color: 'var(--text-primary)'
          }}>
            {isAgentMessage ? getExpertName(message.expert_id) : 'Kullanici'}
          </span>

          {message.is_donor && (
            <span style={{
              padding: '2px 8px',
              borderRadius: '4px',
              background: 'var(--success-color)',
              color: 'white',
              fontSize: '11px',
              fontWeight: 600
            }}>
              BAGISCI
            </span>
          )}

          {message.cycle_number && (
            <span style={{
              padding: '2px 8px',
              borderRadius: '4px',
              background: 'var(--info-color)',
              color: 'white',
              fontSize: '11px',
              fontWeight: 600
            }}>
              Cycle {message.cycle_number}
            </span>
          )}

          <span style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            marginLeft: 'auto'
          }}>
            {formatDate(message.created_at)}
          </span>
        </div>

        {/* Message text */}
        <div style={{
          fontSize: '14px',
          color: 'var(--text-primary)',
          lineHeight: '1.5',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {message.message_text}
        </div>

        {/* Reactions */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '8px',
          flexWrap: 'wrap'
        }}>
          {/* Existing reactions */}
          {message.reactions && Object.entries(message.reactions).map(([emoji, count]) => (
            <button
              key={emoji}
              onClick={() => handleReactionClick(emoji)}
              style={{
                padding: '4px 8px',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--body-bg)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '14px'
              }}
            >
              <span>{emoji}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {count}
              </span>
            </button>
          ))}

          {/* Add reaction button */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowReactionPicker(!showReactionPicker)}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                border: '1px solid var(--border-color)',
                background: 'var(--body-bg)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Smile size={16} color="var(--text-secondary)" />
            </button>

            {showReactionPicker && (
              <ReactionPicker
                onSelect={handleReactionClick}
                onClose={() => setShowReactionPicker(false)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
